import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { displayTime, initials } from "@/lib/format";
import {
  Building2, CheckCircle2, Circle, User, HeartPulse, PillBottle, TestTube, Stethoscope,
  Loader2, ChevronRight, ChevronDown, Save, ArrowRight,
} from "lucide-react";

/**
 * PatientJourney — the traditional EMR worklist. Shows today's appointments at a
 * chosen facility, each patient's current stage, and the action buttons the
 * caller is *entitled* to trigger given their role at that facility.
 * Works for doctors (see all stages) and for staff (see + act on only theirs).
 */
const STAGE_META = {
  booked:        { label: "Booked",        icon: Circle,         tint: "#8A9992" },
  checked_in:    { label: "Checked in",    icon: CheckCircle2,   tint: "#8A5A34" },
  vitals_done:   { label: "Vitals done",   icon: HeartPulse,     tint: "#B14A2A" },
  in_consult:    { label: "In consult",    icon: Stethoscope,    tint: "#0A4C36" },
  consult_done:  { label: "Consult done",  icon: Stethoscope,    tint: "#0A4C36" },
  lab_pending:   { label: "Lab pending",   icon: TestTube,       tint: "#5C7568" },
  lab_done:      { label: "Lab done",      icon: TestTube,       tint: "#0A4C36" },
  lab_referred:  { label: "Lab referred",  icon: TestTube,       tint: "#8A5A34" },
  dispensed:     { label: "Dispensed",     icon: PillBottle,     tint: "#0A4C36" },
  completed:     { label: "Completed",     icon: CheckCircle2,   tint: "#0A4C36" },
};

const STAGE_ORDER = ["booked", "checked_in", "vitals_done", "in_consult", "lab_pending", "lab_done", "dispensed", "completed"];

export default function PatientJourney() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [facilityId, setFacilityId] = useState(params.get("facility") || "");
  const [date, setDate] = useState(params.get("date") || new Date().toISOString().slice(0, 10));
  const [expanded, setExpanded] = useState(null);   // appointment id currently expanded

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (facilityId) q.set("facility_id", facilityId);
      if (date) q.set("date", date);
      const { data } = await api.get(`/worklist/journey?${q.toString()}`);
      setData(data);
      if (!facilityId && data.facilities?.[0]) setFacilityId(data.facilities[0].id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [facilityId, date]);

  const myRoles = data?.my_roles || [];
  const enabledStages = useMemo(
    () => (data?.workflow || []).filter((s) => s.enabled).map((s) => s.key),
    [data]
  );

  const canDo = (stageKey) => {
    if (myRoles.includes("doctor")) return true;
    const roleFor = { reception: "receptionist", vitals: "nurse", lab: "lab_tech", pharmacy: "pharmacist" }[stageKey];
    return roleFor ? myRoles.includes(roleFor) : false;
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-[#5C7568]">Workflow</div>
        <h1 className="text-3xl font-semibold text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Patient workflow</h1>
      </div>

      {loading ? <Loading /> : !data?.facilities?.length ? (
        <div className="card-soft p-12 text-center">
          <div className="text-[#1F2A24] text-lg font-semibold" style={{ fontFamily: "Instrument Sans" }}>You&rsquo;re not assigned to any facility yet</div>
          <div className="text-sm text-[#8A9992] mt-2">Ask your admin/doctor to add you as staff at one of their facilities.</div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <select value={facilityId} onChange={(e) => setFacilityId(e.target.value)} className="input-clean" data-testid="journey-facility">
              {data.facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-clean" data-testid="journey-date" />
            <div className="text-xs text-[#8A9992] ml-2">Your role: <b>{myRoles.join(", ") || "—"}</b></div>
          </div>

          {/* Workflow ribbon — shows the enabled stages at this facility */}
          <div className="rounded-2xl bg-white border border-[#EFECE5] p-4 mb-6 overflow-x-auto" data-testid="workflow-ribbon">
            <div className="flex items-center gap-1 min-w-max">
              {data.workflow.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${s.enabled ? "bg-[#E6F0EC] text-[#0A4C36]" : "bg-[#F3F1EC] text-[#8A9992] line-through"}`}>
                    {i + 1}. {s.label}
                  </div>
                  {i < data.workflow.length - 1 && <ArrowRight size={12} className="text-[#B5C0BA] flex-none" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {data.appointments.length === 0 ? (
            <div className="card-soft p-12 text-center">
              <div className="text-[#8A9992] text-sm">No appointments for {date} at this facility.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {data.appointments.map((a) => {
                const meta = STAGE_META[a.journey_stage] || STAGE_META.booked;
                const Icon = meta.icon;
                const isOpen = expanded === a.id;
                return (
                  <div key={a.id} className="rounded-2xl bg-white border border-[#EFECE5]" data-testid={`journey-row-${a.id}`}>
                    <button onClick={() => setExpanded(isOpen ? null : a.id)} className="w-full text-left px-4 py-3 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-[#FCE7D9] text-[#5C3A21] flex flex-col items-center justify-center flex-none">
                        <div className="text-[9px] tracking-widest">TIME</div>
                        <div className="text-xs font-semibold">{displayTime(a.time)}</div>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center font-semibold text-xs flex-none">
                        {initials(a.patient_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#1F2A24] text-sm truncate" data-testid={`journey-patient-${a.id}`}>{a.patient_name}</div>
                        <div className="text-[11px] text-[#8A9992] truncate">{a.mode}</div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: meta.tint + "18", color: meta.tint }} data-testid={`journey-stage-${a.id}`}>
                        <Icon size={12} /> {meta.label}
                      </span>
                      {isOpen ? <ChevronDown size={16} className="text-[#8A9992] flex-none" /> : <ChevronRight size={16} className="text-[#8A9992] flex-none" />}
                    </button>

                    {isOpen && (
                      <div className="border-t border-[#EFECE5] p-4 space-y-3 bg-[#FDFBF7]" data-testid={`journey-actions-${a.id}`}>
                        <StageActions
                          appt={a}
                          enabledStages={enabledStages}
                          canDo={canDo}
                          onDone={load}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

function StageActions({ appt, enabledStages, canDo, onDone }) {
  const [busy, setBusy] = useState("");
  const call = async (label, fn) => {
    setBusy(label);
    try { await fn(); toast.success(`${label} — done`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3">
      {/* 1. Reception */}
      {enabledStages.includes("reception") && (
        <ActionRow icon={CheckCircle2} title="Check patient in" allowed={canDo("reception")} onClick={() => call("Check-in", async () => api.post(`/appointments/${appt.id}/check-in`))} busy={busy === "Check-in"} completed={STAGE_ORDER.indexOf(appt.journey_stage) >= STAGE_ORDER.indexOf("checked_in")} />
      )}

      {/* 2. Vitals */}
      {enabledStages.includes("vitals") && (
        <VitalsForm appt={appt} allowed={canDo("vitals")} onDone={onDone} />
      )}

      {/* 3. Consultation (doctor only — link to consult flow) */}
      {enabledStages.includes("consultation") && (
        <ActionRow
          icon={Stethoscope} title="Start / open consultation" allowed={canDo("consultation")}
          onClick={() => (window.location.href = `/consultation/new?patient=${appt.patient_id}&appt=${appt.id}`)}
          busy={false}
          ctaLabel="Open"
          completed={STAGE_ORDER.indexOf(appt.journey_stage) >= STAGE_ORDER.indexOf("consult_done")}
        />
      )}

      {/* 4. Lab */}
      {enabledStages.includes("lab") && (
        <LabForm appt={appt} allowed={canDo("lab")} onDone={onDone} />
      )}

      {/* 5. Pharmacy */}
      {enabledStages.includes("pharmacy") && (
        <DispenseForm appt={appt} allowed={canDo("pharmacy")} onDone={onDone} />
      )}

      {/* Complete journey */}
      <ActionRow icon={CheckCircle2} title="Mark journey as completed" allowed={true} onClick={() => call("Complete", async () => api.post(`/appointments/${appt.id}/complete-journey`))} busy={busy === "Complete"} completed={appt.journey_stage === "completed"} ctaLabel="Complete" />
    </div>
  );
}

function ActionRow({ icon: Icon, title, allowed, onClick, busy, completed, ctaLabel = "Do it" }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${completed ? "bg-[#E6F0EC]" : "bg-[#F7F4EC]"}`}>
      <Icon size={16} className={completed ? "text-[#0A4C36]" : "text-[#5C7568]"} />
      <div className="flex-1 text-sm font-medium text-[#1F2A24]">{title}</div>
      {completed ? (
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#0A4C36]">Done</span>
      ) : allowed ? (
        <button onClick={onClick} disabled={busy} className="btn-emerald !py-1.5 !px-3 text-xs">
          {busy ? <Loader2 size={12} className="animate-spin" /> : ctaLabel}
        </button>
      ) : (
        <span className="text-[10px] text-[#8A9992] uppercase tracking-widest">Not your role</span>
      )}
    </div>
  );
}

function VitalsForm({ appt, allowed, onDone }) {
  const v = appt.vitals || {};
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    bp_systolic: v.bp_systolic || "", bp_diastolic: v.bp_diastolic || "",
    heart_rate: v.heart_rate || "", temperature: v.temperature || "",
    spo2: v.spo2 || "", weight_kg: v.weight_kg || "", height_cm: v.height_cm || "",
    notes: v.notes || "",
  });
  const done = !!appt.vitals?.captured_at;
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, val]) => [k, val === "" ? null : (["notes"].includes(k) ? val : Number(val))]));
      await api.patch(`/appointments/${appt.id}/vitals`, body);
      toast.success("Vitals saved"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!open) return <ActionRow icon={HeartPulse} title={done ? `Vitals · ${v.bp_systolic || "?"}/${v.bp_diastolic || "?"} · ${v.temperature || "?"}°F · SpO₂ ${v.spo2 || "?"}%` : "Capture vitals"} allowed={allowed} onClick={() => setOpen(true)} busy={false} completed={done} ctaLabel={done ? "Update" : "Capture"} />;
  return (
    <div className="rounded-xl bg-white border border-[#EFECE5] p-3">
      <div className="text-xs font-semibold text-[#5C7568] mb-2 uppercase tracking-widest">Vitals</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <NumField label="BP systolic"  v={f.bp_systolic}  on={(x) => setF({ ...f, bp_systolic: x })} tid="v-sys" />
        <NumField label="BP diastolic" v={f.bp_diastolic} on={(x) => setF({ ...f, bp_diastolic: x })} tid="v-dia" />
        <NumField label="Heart rate"   v={f.heart_rate}   on={(x) => setF({ ...f, heart_rate: x })} tid="v-hr" />
        <NumField label="Temp (°F)"    v={f.temperature}  on={(x) => setF({ ...f, temperature: x })} tid="v-temp" step="0.1" />
        <NumField label="SpO₂ (%)"     v={f.spo2}         on={(x) => setF({ ...f, spo2: x })} tid="v-spo2" />
        <NumField label="Weight (kg)"  v={f.weight_kg}    on={(x) => setF({ ...f, weight_kg: x })} tid="v-wt" step="0.1" />
        <NumField label="Height (cm)"  v={f.height_cm}    on={(x) => setF({ ...f, height_cm: x })} tid="v-ht" step="0.1" />
      </div>
      <textarea placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="input-clean w-full mt-2 min-h-[52px]" data-testid="v-notes" />
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={() => setOpen(false)} className="btn-outline-emerald text-xs">Cancel</button>
        <button onClick={save} className="btn-emerald text-xs" data-testid="v-save"><Save size={12} /> Save vitals</button>
      </div>
    </div>
  );
}

function LabForm({ appt, allowed, onDone }) {
  const l = appt.lab_order || {};
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ tests: (l.tests || []).join(", "), status: l.status || "pending", referred_to: l.referred_to || "", result_notes: l.result_notes || "" });
  const save = async () => {
    try {
      await api.patch(`/appointments/${appt.id}/lab`, {
        tests: f.tests.split(",").map((s) => s.trim()).filter(Boolean),
        status: f.status, referred_to: f.referred_to, result_notes: f.result_notes,
      });
      toast.success("Lab updated"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const done = ["lab_done", "lab_referred", "dispensed", "completed"].includes(appt.journey_stage);
  if (!open) return <ActionRow icon={TestTube} title={l.tests ? `Lab · ${(l.tests || []).length} test(s) · ${l.status}` : "Lab order"} allowed={allowed} onClick={() => setOpen(true)} busy={false} completed={done} ctaLabel={done ? "Update" : "Process"} />;
  return (
    <div className="rounded-xl bg-white border border-[#EFECE5] p-3">
      <div className="text-xs font-semibold text-[#5C7568] mb-2 uppercase tracking-widest">Lab</div>
      <input value={f.tests} onChange={(e) => setF({ ...f, tests: e.target.value })} placeholder="Tests (comma-separated) e.g. CBC, LFT" className="input-clean w-full" data-testid="l-tests" />
      <div className="grid grid-cols-2 gap-2 mt-2">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="input-clean" data-testid="l-status">
          <option value="pending">Pending</option>
          <option value="done">Done here</option>
          <option value="referred">Referred to other lab</option>
        </select>
        {f.status === "referred" && (
          <input value={f.referred_to} onChange={(e) => setF({ ...f, referred_to: e.target.value })} placeholder="Referred to which lab?" className="input-clean" data-testid="l-referred" />
        )}
      </div>
      <textarea placeholder="Result / notes" value={f.result_notes} onChange={(e) => setF({ ...f, result_notes: e.target.value })} className="input-clean w-full mt-2 min-h-[52px]" data-testid="l-notes" />
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={() => setOpen(false)} className="btn-outline-emerald text-xs">Cancel</button>
        <button onClick={save} className="btn-emerald text-xs" data-testid="l-save"><Save size={12} /> Save lab</button>
      </div>
    </div>
  );
}

function DispenseForm({ appt, allowed, onDone }) {
  const d = appt.dispense || {};
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(d.items || [{ medication: "", qty: 1, unit_price: 0 }]);
  const [notes, setNotes] = useState(d.notes || "");
  const total = useMemo(() => items.reduce((s, i) => s + (Number(i.qty || 0) * Number(i.unit_price || 0)), 0), [items]);
  const done = ["dispensed", "completed"].includes(appt.journey_stage);
  const save = async () => {
    try {
      await api.patch(`/appointments/${appt.id}/dispense`, { items, total_amount: total, notes });
      toast.success("Medicines dispensed"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!open) return <ActionRow icon={PillBottle} title={done ? `Dispensed · ₹${(d.total_amount || 0).toFixed(2)}` : "Dispense medicines & bill"} allowed={allowed} onClick={() => setOpen(true)} busy={false} completed={done} ctaLabel={done ? "Update" : "Dispense"} />;
  return (
    <div className="rounded-xl bg-white border border-[#EFECE5] p-3">
      <div className="text-xs font-semibold text-[#5C7568] mb-2 uppercase tracking-widest">Pharmacy</div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-6 gap-2 items-center">
            <input value={it.medication} onChange={(e) => { const next=[...items]; next[i].medication=e.target.value; setItems(next); }} placeholder="Medication" className="input-clean col-span-3" data-testid={`ph-med-${i}`} />
            <input type="number" value={it.qty} onChange={(e) => { const next=[...items]; next[i].qty=e.target.value; setItems(next); }} placeholder="Qty" className="input-clean" data-testid={`ph-qty-${i}`} />
            <input type="number" step="0.01" value={it.unit_price} onChange={(e) => { const next=[...items]; next[i].unit_price=e.target.value; setItems(next); }} placeholder="₹/unit" className="input-clean" data-testid={`ph-price-${i}`} />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-[#B14A2A] text-sm">×</button>
          </div>
        ))}
        <button onClick={() => setItems([...items, { medication: "", qty: 1, unit_price: 0 }])} className="text-xs text-[#0A4C36] underline" data-testid="ph-add">+ Add item</button>
      </div>
      <textarea placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="input-clean w-full mt-2 min-h-[52px]" data-testid="ph-notes" />
      <div className="flex items-center justify-between mt-2">
        <div className="text-sm text-[#1F2A24]">Total: <b>₹ {total.toFixed(2)}</b></div>
        <div className="flex gap-2">
          <button onClick={() => setOpen(false)} className="btn-outline-emerald text-xs">Cancel</button>
          <button onClick={save} className="btn-emerald text-xs" data-testid="ph-save"><Save size={12} /> Dispense & bill</button>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, v, on, tid, step }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-[#5C7568] mb-1">{label}</div>
      <input type="number" step={step || "1"} value={v} onChange={(e) => on(e.target.value)} className="input-clean w-full" data-testid={tid} />
    </label>
  );
}

function Loading() { return <div className="py-16 flex items-center justify-center text-[#8A9992]"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>; }
